import React from 'react';
import { ChevronDown, Rocket } from 'lucide-react';

const normalizeTypeKey = (raw) => {
  if (raw == null) return null;
  const v = String(raw).trim().toLowerCase();
  return v || null;
};

const PENDING_OR_SHOWN_STATUSES = new Set(['NEW', 'SHOWN', 'LIKED']);

const isPendingOrShownStatus = (status) => {
  if (status == null || status === '') return true; // default: consider "pendiente"
  return PENDING_OR_SHOWN_STATUSES.has(String(status).trim().toUpperCase());
};

const countUniqueOptimizationTypes = (optimizations) => {
  if (!Array.isArray(optimizations) || optimizations.length === 0) return 0;

  // Shape A: API groups [{ rule, suggestions: [...] }]
  const looksLikeGroups = Array.isArray(optimizations[0]?.suggestions);
  if (looksLikeGroups) {
    const keys = new Set();
    optimizations.forEach((g) => {
      const groupRuleKey = normalizeTypeKey(g?.rule?.key ?? g?.rule?.id ?? null);
      (g?.suggestions || []).forEach((s) => {
        if (!isPendingOrShownStatus(s?.status)) return;
        const k = normalizeTypeKey(s?.rule?.key ?? s?.action_type ?? groupRuleKey ?? null);
        if (k) keys.add(k);
      });
    });
    return keys.size;
  }

  // Shape B: flat list of cards/suggestions
  const keys = new Set();
  optimizations.forEach((o) => {
    if (!isPendingOrShownStatus(o?.status)) return;
    const k = normalizeTypeKey(o?.rule_key ?? o?.rule?.key ?? o?.action_type ?? null);
    if (k) keys.add(k);
  });
  return keys.size;
};

const OptimizationsCell = ({
  optimizations = [],
  hasObjectives = false,
  hasAccountConnected = true,
  onGenerateClick,
  onConnectClick,
  isGenerating = false,
  mustWaitForNextUpdate = false,
  isExpanded = false,
  onToggle,
  onNavigate,
}) => {
  // Sin cuenta conectada: mensaje que habla de objetivos (clickeable si hay callback)
  if (!hasAccountConnected) {
    if (typeof onConnectClick === 'function') {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onConnectClick();
          }}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white rounded-full transition-colors backdrop-blur-xl bg-gradient-to-r from-cyan-500/20 via-teal-500/20 to-cyan-600/20 border border-cyan-400/30 hover:border-cyan-400/50 cursor-pointer"
        >
          Conecta una cuenta para generar optimizaciones
        </button>
      );
    }
    return (
      <span className="text-xs text-white/70">Conecta una cuenta para generar optimizaciones</span>
    );
  }

  // Sin objetivos: mensaje fijo
  if (!hasObjectives) {
    return (
      <span className="text-xs text-white/70">Necesitas objetivos para generar las Optimizaciones</span>
    );
  }

  // Count unique optimization "types" (rule key) and only statuses pendientes/vistas.
  const count = countUniqueOptimizationTypes(optimizations);

  // Con objetivos, sin optimizaciones
  if (count === 0) {
    if (mustWaitForNextUpdate) {
      return (
        <span className="text-xs text-white/70">Ups, no encontramos suficiente data para generar optimizaciones</span>
      );
    }
    if (typeof onGenerateClick === 'function') {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onGenerateClick();
          }}
          disabled={isGenerating}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white rounded-full backdrop-blur-xl bg-gradient-to-r from-fuchsia-500/20 via-purple-500/20 to-blue-500/20 border border-fuchsia-400/30 hover:border-fuchsia-400/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Rocket size={14} className="text-fuchsia-300 shrink-0" />
          {isGenerating ? 'Generando optimizaciones...' : 'Generar optimizaciones IA AHORA 🚀'}
        </button>
      );
    }
    return (
      <span className="text-xs text-white/70">Estamos generando tus optimizaciones 💪</span>
    );
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (typeof onNavigate === 'function') {
          onNavigate();
          return;
        }
        onToggle?.();
      }}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-gradient-to-r from-fuchsia-500/30 via-purple-500/30 to-blue-500/30 hover:from-fuchsia-500/40 hover:via-purple-500/40 hover:to-blue-500/40 backdrop-blur-md border border-fuchsia-400/40 shadow-[0_4px_14px_-2px_rgba(217,70,239,0.4),0_4px_20px_-4px_rgba(99,102,241,0.5)] hover:shadow-[0_8px_25px_-2px_rgba(217,70,239,0.5),0_8px_30px_-4px_rgba(99,102,241,0.6)] transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
    >
      <span className="bg-gradient-to-r from-fuchsia-300 via-purple-200 to-blue-300 bg-clip-text text-transparent">
        {count} tipos de optimización
      </span>
      {typeof onNavigate !== 'function' && (
        <ChevronDown
          size={18}
          className={`text-fuchsia-200 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      )}
    </button>
  );
};

export default OptimizationsCell;
